import {
  DataTableSortableColumn,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuItem,
  TooltipContent,
  TooltipTrigger,
  DropdownMenu,
  Typography,
  ColumnDef,
  Checkbox,
  Tooltip,
  Button,
  Badge,
} from '@ui/components';
import { EmployeeTableDataDto, ServiceType } from '@interfaces/models';
import { MenuVertical } from '@ui/components/icons';
import { formatPhoneNumber } from '@helpers/utils';
import { HasPermission } from '@shared/components';
import { ServiceIcons } from '@shared/utils';
import { TFunction } from 'i18next';

import { UpdateEmployeeProfileSelect } from '../update-employee-profile-select';

export const getEmployeesColumns = (
  t: TFunction,
  onEditEmployee: (employee: EmployeeTableDataDto) => void,
  onRemoveEmployee: (employee: EmployeeTableDataDto) => void,
): ColumnDef<EmployeeTableDataDto>[] => [
  {
    id: 'select',
    size: 50,
    header: ({ table }) => (
      <Checkbox
        onClick={table.getToggleAllRowsSelectedHandler()}
        checked={table.getIsAllRowsSelected()}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        checked={row.getIsSelected()}
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'firstName',
    header: ({ column }) => {
      return (
        <DataTableSortableColumn label={t('EMPLOYEES.TABLE_HEADER_FIRST_NAME')} column={column} />
      );
    },
    cell: ({ row }) => {
      const { firstName } = row.original;
      return firstName ?? '-';
    },
  },
  {
    accessorKey: 'lastName',
    maxSize: 300,
    header: ({ column }) => {
      return (
        <DataTableSortableColumn label={t('EMPLOYEES.TABLE_HEADER_LAST_NAME')} column={column} />
      );
    },
    cell: ({ row }) => {
      const { lastName } = row.original;
      return lastName ?? '-';
    },
  },
  {
    accessorKey: 'phoneNumber',
    header: ({ column }) => {
      return (
        <DataTableSortableColumn label={t('EMPLOYEES.TABLE_HEADER_PHONE_NUMBER')} column={column} />
      );
    },
    cell: ({ row }) => {
      const { phoneNumber, phoneExt } = row.original;

      if (!phoneNumber || !phoneExt) {
        return '-';
      }

      return formatPhoneNumber(phoneNumber, phoneExt);
    },
  },
  {
    accessorKey: 'email',
    maxSize: 300,
    header: ({ column }) => {
      return <DataTableSortableColumn label={t('EMPLOYEES.TABLE_HEADER_EMAIL')} column={column} />;
    },
  },
  {
    accessorKey: 'assignedVehicles',
    maxSize: 200,
    header: ({ column }) => {
      return (
        <DataTableSortableColumn
          label={t('EMPLOYEES.TABLE_HEADER_ASSIGNED_VEHICLES')}
          column={column}
        />
      );
    },
    cell: ({ row }) => {
      const assignedVehicles = row.original.assignedVehicles ?? [];
      const assignedVehiclesCount = assignedVehicles.length;

      if (!assignedVehiclesCount) {
        return '-';
      }

      const vehiclesText = assignedVehicles.map((vehicle) => vehicle.registrationNumber).join(', ');

      if (assignedVehiclesCount <= 1) {
        return vehiclesText;
      }

      return (
        <Tooltip>
          <TooltipTrigger className="truncate max-w-[100px]">{vehiclesText}</TooltipTrigger>
          <TooltipContent className="bg-background text-default flex gap-2 w-auto max-w-[11rem] p-3">
            <Typography variant="labelMediumBook">{vehiclesText}</Typography>
          </TooltipContent>
        </Tooltip>
      );
    },
  },
  {
    accessorKey: 'labels',
    header: t('EMPLOYEES.TABLE_HEADER_DEPARTMENT'),
    cell: ({ row }) => {
      const labels = row.original.labels ?? [];
      const labelCount = labels.length;

      if (!labelCount) {
        return '-';
      }

      return (
        <div className="flex items-center gap-1 whitespace-nowrap">
          <Badge size="large" outlined>
            {labels[0]?.name}
          </Badge>
          {labelCount > 1 && (
            <Tooltip>
              <TooltipTrigger>
                <Badge size="large" outlined icon>
                  +{labelCount - 1}
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="bg-background flex gap-2 w-auto max-w-[15rem] flex-wrap">
                {labels.map((label) => (
                  <Badge key={label.id} size="large" outlined>
                    {label.name}
                  </Badge>
                ))}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'profiles',
    header: t('EMPLOYEES.TABLE_HEADER_PROFILE'),
    cell: ({ row }) => {
      const employeeId = row.original.id;
      const employeeProfiles = row.original.profiles ?? [];

      const employeeProfileCount = employeeProfiles.length;
      if (!employeeProfileCount) {
        return '-';
      }

      return (
        <UpdateEmployeeProfileSelect employeeProfiles={employeeProfiles} employeeId={employeeId} />
      );
    },
  },
  {
    accessorKey: 'services',
    header: t('EMPLOYEES.TABLE_HEADER_SERVICES'),
    cell: ({ row }) => {
      const services = row.original.services ?? [];
      const serviceCount = services.length;

      if (!services || !serviceCount) {
        return '-';
      }

      return (
        <div className="flex items-center gap-1 whitespace-nowrap">
          <Badge className="[&>svg]:size-4" size="large" outlined>
            {ServiceIcons[services[0]?.type as ServiceType]}
            {t(`SERVICE_TYPE.${services[0]?.type}`)}
          </Badge>
          {serviceCount > 1 && (
            <Tooltip>
              <TooltipTrigger>
                <Badge size="large" outlined>
                  +{serviceCount - 1}
                </Badge>
              </TooltipTrigger>
              <TooltipContent className="bg-background flex gap-2 w-auto max-w-[15rem] flex-wrap">
                {services.map((service) => {
                  return (
                    <Badge className="[&>svg]:size-4" key={service.id} size="large" outlined>
                      {ServiceIcons[service.type]}
                      {t(`SERVICE_TYPE.${service.type}`)}
                    </Badge>
                  );
                })}
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: 'actions',
    header: '',
    size: 100,
    cell: ({ row }) => {
      const rowData = row.original;
      return (
        <div className="flex justify-end">
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger tabIndex={-1} asChild>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost">
                    <MenuVertical />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent className="w-auto">
                <Typography variant="labelXSmallBook">
                  {t('EMPLOYEES.TABLE_MORE_ACTIONS')}
                </Typography>
              </TooltipContent>
            </Tooltip>
            <DropdownMenuContent className="w-56">
              <HasPermission permissionFeature="EMPLOYEES_EDIT_EMPLOYEE" permissionType="READ">
                <DropdownMenuItem onClick={() => onEditEmployee(rowData)}>
                  {t('EMPLOYEES.TABLE_EDIT_EMPLOYEE')}
                </DropdownMenuItem>
              </HasPermission>
              <HasPermission permissionFeature="EMPLOYEES_REMOVE_EMPLOYEE" permissionType="WRITE">
                <DropdownMenuItem onClick={() => onRemoveEmployee(rowData)}>
                  {t('EMPLOYEES.TABLE_DELETE_EMPLOYEE')}
                </DropdownMenuItem>
              </HasPermission>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      );
    },
  },
];
